import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import compose from 'lodash.flowright';
import pick from 'lodash/pick';
import PropTypes from 'prop-types';
import { useQuery, withApollo } from 'react-apollo';
import Select from 'react-select';

import { locationSearch, locationData as locationDataQuery } from './graphql';
import useDebounce from '../../../hooks/useDebounce';
import Label from '../Label';
import getLogger from '../../../util/logger';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { getErrorString } from '../../../util/normalize-errors';

const log = getLogger('LocationInput');

const NEAR_USER_VALUE = 'NEAR_USER_VALUE';

const propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string,
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  inputRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  disabled: PropTypes.bool,
  className: PropTypes.string,
  size: PropTypes.oneOf(['sm']),
  required: PropTypes.bool,
  value: PropTypes.object,
  onChange: PropTypes.func.isRequired,
  /**
   * Called with place object:
   * {
   *  id: "ChIJY5xLvPz-v1QRwlcDj-ApNPk",
   *  name: "Salem",
   *  description: "Oregon, USA",
   *  ?stateCode: "OR" (setif `appendLocationData` prop is true)
   * }
   * or null if the input was cleared.
   */
  onBlur: PropTypes.func,
  label: PropTypes.node,
  labelNote: PropTypes.string,
  labelTip: PropTypes.string,
  labelSrOnly: PropTypes.bool,
  labelType: PropTypes.string,
  appendLocationData: PropTypes.bool,
  client: PropTypes.shape({
    query: PropTypes.func.isRequired,
  }).isRequired,
  nearUserSearchEnabled: PropTypes.bool,
  selectOnBlur: PropTypes.bool,
  optional: PropTypes.bool,
};

const LocationInput = ({
  id,
  name,
  label,
  labelNote,
  labelTip,
  labelSrOnly,
  labelType,
  className,
  placeholder,
  inputRef,
  disabled,
  size,
  required,
  value,
  onChange,
  onBlur,
  appendLocationData = false,
  nearUserSearchEnabled = false,
  client,
  selectOnBlur,
  optional,
  ...props
}) => {
  const openAlert = useOpenAlert();
  const classes = classNames({
    'location-input': true,
    [className]: !!className,
  });

  const [enteredValue, setEnteredValue] = useState('');
  const [debouncedValue] = useDebounce(enteredValue, 300);
  const [places, setPlaces] = useState([]);
  const [options, setOptions] = useState([]);
  const [loadingState, setLoadingState] = useState(false);

  const { loading: loadingLocations, data } = useQuery(locationSearch, {
    skip: debouncedValue.length < 3,
    variables: { query: debouncedValue },
  });

  const loading = loadingLocations || loadingState;

  useEffect(() => {
    const geoResults = data?.locationSearch?.records ?? [];

    setPlaces(geoResults);

    const formattedOptions = geoResults.map(result => ({
      name: result.name,
      description: result.description,
      value: result.id,
    }));

    setOptions(formattedOptions);
  }, [data]);

  const getDataFromLocationId = async locationId => {
    setLoadingState(true);
    try {
      const {
        data: { location: locationData },
      } = await client.query({
        query: locationDataQuery,
        variables: {
          locationId,
        },
      });

      return locationData;
    } catch (err) {
      log.error(
        'getDataFromLocationId() :: Error getting location details:',
        err,
      );

      openAlert({
        type: 'error',
        message: getErrorString(err.message),
      });
    } finally {
      setLoadingState(false);
    }
    return undefined;
  };

  const onRequestGeolocation = () => {
    const onSuccess = geolocationData => {
      onChange({
        // We build a pseudo location object - without an ID.
        // The only attribute required to get photographers near
        // is the point.
        id: NEAR_USER_VALUE, // This is no google location, make sure ID is null is set to something we can go back on.
        name: 'Near me',
        description: '',
        locationData: {
          id: NEAR_USER_VALUE,
          point: {
            latitude: geolocationData.coords.latitude,
            longitude: geolocationData.coords.longitude,
          },
        },
      });
    };

    const onError = err => {
      // Most likely error is the user rejecting
      // the provide geolocation to the browser.
      openAlert({
        type: 'error',
        message: err.message,
      });
    };

    if (!navigator.geolocation) {
      openAlert({
        type: 'error',
        message: 'Geolocation is not supported by your browser',
      });
    } else {
      navigator.geolocation.getCurrentPosition(onSuccess, onError);
    }
  };

  const selectPlace = async selectedPlaceValue => {
    if (selectedPlaceValue?.value === NEAR_USER_VALUE) {
      onRequestGeolocation();
      return;
    }

    // Handle user merely erasing the text.
    if (selectedPlaceValue) {
      const selectedPlace = places.find(
        place => place.id === selectedPlaceValue.value,
      );
      // Handle user changing text but not selecting a viable option.
      if (!selectedPlace) {
        onChange(null);
        return;
      }

      const returnSelectedLocation = await (async () => {
        const defaultLocationProps = {
          ...pick(selectedPlace, ['description', 'id', 'name']),
        };

        if (appendLocationData) {
          // Call api to get state
          const locationData = await getDataFromLocationId(selectedPlace.id);
          if (locationData) {
            return {
              ...defaultLocationProps,
              locationData,
            };
          }

          return null;
        }

        return defaultLocationProps;
      })();

      onChange(returnSelectedLocation);
    } else {
      // Deselect value. User clicked 'x' or double backspaced in input.
      onChange(null);
      setEnteredValue('');
    }
  };

  return (
    <div className={classes}>
      {label && (
        <Label
          id={id}
          label={label}
          labelNote={labelNote}
          labelTip={labelTip}
          labelSrOnly={labelSrOnly}
          labelType={labelType}
          required={required}
          optional={optional}
        />
      )}
      <Select
        id={id}
        placeholder={placeholder}
        isLoading={loading}
        isClearable
        noOptionsMessage={({ inputValue }) => {
          if (loading) {
            return 'Loading...';
          }

          if (!inputValue) {
            return 'Start typing and then select a location';
          }

          if (inputValue.length < 3) {
            return 'Please type  at least 3 characters for us to match a location...';
          }

          return 'We could not find any location that match your criteria...';
        }}
        onInputChange={selectedPlace => {
          setEnteredValue(selectedPlace);
        }}
        onChange={selectPlace}
        onBlur={event => {
          if (selectOnBlur && options.length > 0) {
            selectPlace(options[0]);
          }

          // Sort of a hack so that the event.target.name can be
          // whatever the user provides in props. Looks like react-select@2.4.1
          // always returns a default value that cannot be modified.
          onBlur?.({
            ...event,
            target: {
              ...event.target,
              name: name || event.target.name,
            },
          });
        }}
        options={
          nearUserSearchEnabled
            ? [
                {
                  name: 'Near my location',
                  value: NEAR_USER_VALUE,
                },
                ...options,
              ]
            : options
        }
        inputValue={enteredValue}
        filterOption={() => true} // Don't require match. So for instance 'salem or' will result in 'Salem (OR)' - trust API to filter.
        formatOptionValue={record => <span>{record.name}</span>}
        formatOptionLabel={(record, { context, selectValue }) => {
          const isSelected = selectValue.some(v => v.value === record.value);

          return (
            <span
              className={classNames({
                'location-input-option': true,
                'location-input-option-value': context === 'value', // Rendering the result in the search box
                'is-selected': isSelected && context === 'menu',
              })}
            >
              <span className="location-input-option-name">{record.name}</span>
              <span className="location-input-option-desc">
                {record.description}
              </span>
            </span>
          );
        }}
        {...props}
        value={value}
      />
    </div>
  );
};

LocationInput.propTypes = propTypes;

export default compose(withApollo)(LocationInput);
